import {
  CompositionThemeContext,
  type CompositionTheme,
} from "../design/CompositionThemeContext.js";
import {
  readCompositionAppearance,
  presentCatalogueRenderNodes,
  type CompositionAppearance,
} from "../../../util/src/design/catalogue-runtime.js";
import {
  compatibleVisualVariant,
  presentRenderNodes,
  type VisualDocumentVariant,
} from "../../../util/src/design/visual-variants.js";
import { presentCompositionRuntimeColors } from "../../../util/src/design/catalogue-variants.js";
import type { InterfaceColorMode } from "../../../util/src/design/tokens.js";
import { useBehaviorTimers } from "../behavior-timers/useBehaviorTimers";
import { behaviorTimerClient } from "../../../util/src/behavior-timers/client";
import {
  isTimerAction,
  type TimerCommand,
} from "../../../util/src/behavior-timers/protocol";
import { localizeCustomDisciplineDocument } from "../../../util/src/custom-disciplines/labels.js";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  CompiledLayoutRuntimeState,
  RenderNode,
  RuntimeControlRoomDispatch,
  RuntimeCustomDisciplineClickDispatch,
  RuntimeEffect,
  RuntimeFlowContinuation,
} from "../../../util/src/layout-compiler/types.js";
import type {
  InjectedData,
  LayoutDocument,
} from "../../../util/src/layout/types.js";
import {
  compileDocument,
  createCompiledLayoutRuntimeState,
  invalidateCompiledLayoutRuntimeState,
  resetCompiledLayoutRuntimeState,
} from "../../../util/src/layout-compiler/compileDocument.js";
import {
  useLayoutFontFacesProvider,
  type GetLayoutFontFaces,
} from "../display-editor/DataProviderContext.js";
import {
  ProtectedMediaCacheContext,
  useProtectedMediaCache,
} from "../display-editor/ProtectedMediaCacheContext.js";
import { useLayoutFontFacesLoader } from "../display-editor/useLayoutFontFacesLoader.js";
import { collectMediaSources } from "./collectMediaSources.js";
import { collectRenderNodesByKey } from "./collectRenderNodesByKey.js";
import { playCompiledAnimationEffects } from "./playCompiledAnimationEffects.js";
import { CompiledLayoutFontFacesProvider } from "./CompiledLayoutFontFacesContext.js";
import { CompiledLayoutRuntimeProvider } from "./CompiledLayoutRuntimeContext.js";
import {
  CompiledLayoutFillContext,
  type CompiledLayoutFillContextValue,
} from "./CompiledLayoutFillContext.js";
import { RenderNodeView } from "./RenderNodeView.js";
import { getSiblingRenderReactKey } from "./renderReactKey.js";
import {
  indexRuntimeInteractions,
  needsRuntimeButtonSemantics,
  runtimeClickTargets,
  RUNTIME_INTERACTION_CSS,
} from "./runtimeNodeInteractions.js";

type CustomDisciplineActionEffect = Extract<
  RuntimeEffect,
  { type: "customDiscipline.action" }
>;

type CompiledLayoutRendererProps = {
  document: LayoutDocument;
  data: InjectedData;
  scale?: number;
  isEditor?: boolean;
  /** Share immutable cell programs; each renderer keeps its own runtime state. */
  reuseProgram?: boolean;
  controlRoomDispatches?: RuntimeControlRoomDispatch[];
  runtimeInvalidation?: {
    revision: number;
    nodeIds: readonly string[];
  };
  /** Lists and loads user-uploaded layout fonts (same contract as {@link LayoutRenderer}). */
  getLayoutFontFaces?: GetLayoutFontFaces;
  /**
   * Called once per click for each `customDiscipline.action` effect produced by a
   * layout element's `onClick` behavior (e.g. `customListSetField`,
   * `customOpenModal`). The host page applies the mutation exactly once per call.
   */
  onCustomDisciplineAction?: (effect: CustomDisciplineActionEffect) => void;
  /** Deliver one click’s ordered effects together so the host can persist adjacent writes atomically. */
  onCustomDisciplineActions?: (effects: CustomDisciplineActionEffect[]) => void;
  /** Reports a user edit on a `customDisciplineInput` field (custom discipline modals). */
  onCustomDisciplineInputChange?: (fieldKey: string, value: string) => void;
  /** Optional prefix for nested layout instances so React keys/runtime state stay isolated. */
  nodeInstanceKeyPrefix?: string;
  /**
   * When true, the canvas and scene root stretch to the host container (100% width/height)
   * instead of using fixed pixel dimensions from the layout document.
   */
  fillParent?: boolean;
  /** Natural height for a tooltip whose root is a relative flex container. */
  fitContentHeight?: boolean;
  /** Style the existing canvas host without adding a clipping/stacking wrapper. */
  canvasStyle?: React.CSSProperties;
  /** Render-only appearance: never recompiles or restarts the original Behavior. */
  visualVariant?: VisualDocumentVariant;
  /** Opt-in palette for authored composition previews; business effects stay unchanged. */
  compositionColorMode?: InterfaceColorMode;
  catalogueTheme?: CompositionTheme;
  compositionAppearance?: CompositionAppearance;
};

type AnimationPlayEffect = Extract<RuntimeEffect, { type: "animation.play" }>;

function CompiledLayoutRendererInner({
  document,
  data,
  scale = 1,
  isEditor = false,
  reuseProgram = false,
  fillParent = false,
  fitContentHeight = false,
  canvasStyle,
  visualVariant,
  compositionColorMode,
  catalogueTheme,
  compositionAppearance,
  controlRoomDispatches,
  runtimeInvalidation,
  onCustomDisciplineAction,
  onCustomDisciplineActions,
  onCustomDisciplineInputChange,
  nodeInstanceKeyPrefix,
}: Omit<CompiledLayoutRendererProps, "getLayoutFontFaces">) {
  const runtimeElementByKeyRef = useRef(new Map<string, HTMLElement>());
  const renderNodeByKeyRef = useRef(new Map<string, RenderNode>());
  const editorRuntimeStateRef = useRef<CompiledLayoutRuntimeState>(
    createCompiledLayoutRuntimeState(),
  );
  const wakeContinuationsRef = useRef(
    new Map<string, RuntimeFlowContinuation>(),
  );
  const wakeTimeoutRef = useRef<number | null>(null);
  const previousDocumentRuntimeKeyRef = useRef<string | null>(null);
  const previousDocumentIdRef = useRef<string | null>(null);
  const previousInvalidationRevisionRef = useRef<number | null>(null);
  const committedControlRoomDispatchIdsRef = useRef(new Set<number>());
  const committedCustomDisciplineClickDispatchIdsRef = useRef(
    new Set<number>(),
  );
  const customDisciplineClickDispatchCounterRef = useRef(0);
  const targetedBehaviorNodeIdsForEvaluateRef = useRef<
    readonly string[] | null
  >(null);
  const committedWakeVersionRef = useRef(0);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [wakeRun, setWakeRun] = useState<{
    continuations: RuntimeFlowContinuation[];
    version: number;
  }>({ continuations: [], version: 0 });
  const [customDisciplineClickDispatches, setCustomDisciplineClickDispatches] =
    useState<RuntimeCustomDisciplineClickDispatch[]>([]);

  const dispatchCustomDisciplineClick = useCallback(
    (nodeId: string, nodeKey?: string) => {
      customDisciplineClickDispatchCounterRef.current += 1;
      const dispatch: RuntimeCustomDisciplineClickDispatch = {
        id: customDisciplineClickDispatchCounterRef.current,
        nodeId,
        nodeKey,
      };
      setCustomDisciplineClickDispatches((current) => [...current, dispatch]);
    },
    [],
  );

  const clearWakeTimeout = useCallback(() => {
    if (wakeTimeoutRef.current != null) {
      window.clearTimeout(wakeTimeoutRef.current);
      wakeTimeoutRef.current = null;
    }
  }, []);

  const scheduleNextWake = useCallback(() => {
    clearWakeTimeout();

    let nextWakeAt: number | undefined;
    for (const continuation of wakeContinuationsRef.current.values()) {
      if (nextWakeAt == null || continuation.wakeAt < nextWakeAt) {
        nextWakeAt = continuation.wakeAt;
      }
    }

    if (nextWakeAt == null) {
      return;
    }

    const delayMs = Math.max(0, nextWakeAt - Date.now());
    wakeTimeoutRef.current = window.setTimeout(() => {
      const now = Date.now();
      const due: RuntimeFlowContinuation[] = [];

      for (const [key, continuation] of wakeContinuationsRef.current) {
        if (continuation.wakeAt <= now) {
          due.push(continuation);
          wakeContinuationsRef.current.delete(key);
        }
      }

      if (due.length > 0) {
        setWakeRun((current) => ({
          continuations: due,
          version: current.version + 1,
        }));
        return;
      }

      scheduleNextWake();
    }, delayMs);
  }, [clearWakeTimeout]);

  const registerNodeElement = useCallback(
    (key: string, element: HTMLElement | null, node: RenderNode) => {
      if (!element) {
        runtimeElementByKeyRef.current.delete(key);
        renderNodeByKeyRef.current.delete(key);
        return;
      }

      runtimeElementByKeyRef.current.set(key, element);
      renderNodeByKeyRef.current.set(key, node);
    },
    [],
  );

  const removeWakeContinuationsForNodeIds = useCallback(
    (nodeIds: readonly string[]) => {
      const targetNodeIds = new Set(nodeIds.filter(Boolean));
      if (targetNodeIds.size === 0) {
        return;
      }

      const keyTouchesNode = (key: string, nodeId: string) =>
        key === nodeId ||
        key.startsWith(`${nodeId}:`) ||
        key.endsWith(`:${nodeId}`) ||
        key.includes(`:${nodeId}:`) ||
        key.includes(`|${nodeId}|`) ||
        key.includes(`|${nodeId}:`);

      for (const [key, continuation] of wakeContinuationsRef.current) {
        let shouldDelete = false;
        for (const nodeId of targetNodeIds) {
          if (
            continuation.nodeId === nodeId ||
            keyTouchesNode(key, nodeId) ||
            keyTouchesNode(continuation.flowKey, nodeId) ||
            (continuation.resumeTarget?.ownerStateKey &&
              keyTouchesNode(continuation.resumeTarget.ownerStateKey, nodeId))
          ) {
            shouldDelete = true;
            break;
          }
        }

        if (shouldDelete) {
          wakeContinuationsRef.current.delete(key);
        }
      }

      clearWakeTimeout();
    },
    [clearWakeTimeout],
  );

  const compiledDocument = useMemo(() => {
    targetedBehaviorNodeIdsForEvaluateRef.current = null;

    if (!isEditor) {
      return compileDocument(document, { reuseProgram });
    }

    const runtimeState = editorRuntimeStateRef.current;
    if (previousDocumentIdRef.current !== document.meta.id) {
      resetCompiledLayoutRuntimeState(runtimeState);
      wakeContinuationsRef.current.clear();
      clearWakeTimeout();
      previousDocumentIdRef.current = document.meta.id;
      previousInvalidationRevisionRef.current = null;
    }

    const shouldInvalidate =
      runtimeInvalidation &&
      runtimeInvalidation.revision !== previousInvalidationRevisionRef.current;

    if (shouldInvalidate) {
      invalidateCompiledLayoutRuntimeState(
        runtimeState,
        document,
        runtimeInvalidation.nodeIds,
      );
      removeWakeContinuationsForNodeIds(runtimeInvalidation.nodeIds);
      previousInvalidationRevisionRef.current = runtimeInvalidation.revision;
      targetedBehaviorNodeIdsForEvaluateRef.current =
        runtimeInvalidation.nodeIds;
    }

    return compileDocument(document, { runtimeState });
  }, [
    clearWakeTimeout,
    document,
    isEditor,
    reuseProgram,
    removeWakeContinuationsForNodeIds,
    runtimeInvalidation,
  ]);
  const documentRuntimeKey = `${document.meta.id}:${document.version}:${document.meta.updatedAt}`;

  useEffect(() => {
    return clearWakeTimeout;
  }, [clearWakeTimeout]);

  const behaviorTimers = useBehaviorTimers(
    data,
    !isEditor && compiledDocument.dependencies.includes("behaviorTimers"),
  );
  const consumedTimerEffects = useRef(new WeakSet<object>());
  const renderModel = useMemo(() => {
    if (previousDocumentRuntimeKeyRef.current !== documentRuntimeKey) {
      const shouldResetWakeState =
        !isEditor || previousDocumentIdRef.current !== document.meta.id;

      if (shouldResetWakeState) {
        wakeContinuationsRef.current.clear();
        clearWakeTimeout();
        committedWakeVersionRef.current = wakeRun.version;
      }
      committedControlRoomDispatchIdsRef.current.clear();
      committedCustomDisciplineClickDispatchIdsRef.current.clear();
      previousDocumentRuntimeKeyRef.current = documentRuntimeKey;
    }

    const resumeContinuations =
      wakeRun.version !== committedWakeVersionRef.current
        ? wakeRun.continuations
        : [];
    const targetedBehaviorNodeIdList =
      targetedBehaviorNodeIdsForEvaluateRef.current;
    targetedBehaviorNodeIdsForEvaluateRef.current = null;
    const targetedBehaviorNodeIds =
      isEditor && targetedBehaviorNodeIdList?.length
        ? new Set(targetedBehaviorNodeIdList)
        : undefined;

    const pendingControlRoomDispatches = controlRoomDispatches?.filter(
      (dispatch) =>
        !committedControlRoomDispatchIdsRef.current.has(dispatch.id),
    );

    const pendingCustomDisciplineClickDispatches =
      customDisciplineClickDispatches.filter(
        (dispatch) =>
          !committedCustomDisciplineClickDispatchIdsRef.current.has(
            dispatch.id,
          ),
      );

    const evaluated = compiledDocument.evaluate({
      ...data,
      behaviorTimers: data.behaviorTimers ?? behaviorTimers,
      __nodeInstanceKeyPrefix: nodeInstanceKeyPrefix,
      __isEditor: isEditor,
      __targetedBehaviorNodeIds: targetedBehaviorNodeIds,
      __fastForwardAsyncFlows: targetedBehaviorNodeIds != null,
      __nowMs: Date.now(),
      __resumeContinuations: resumeContinuations,
      __controlRoomDispatches: pendingControlRoomDispatches,
      __committedControlRoomDispatchIds:
        committedControlRoomDispatchIdsRef.current,
      __deferControlRoomDispatchConsumption: true,
      __customDisciplineClickDispatches: pendingCustomDisciplineClickDispatches,
      __committedCustomDisciplineClickDispatchIds:
        committedCustomDisciplineClickDispatchIdsRef.current,
    });

    return evaluated;
  }, [
    behaviorTimers,
    clearWakeTimeout,
    compiledDocument,
    controlRoomDispatches,
    customDisciplineClickDispatches,
    data,
    documentRuntimeKey,
    document.meta.id,
    isEditor,
    nodeInstanceKeyPrefix,
    wakeRun,
  ]);

  useEffect(() => {
    if (isEditor) return;
    for (const effect of renderModel?.effects ?? []) {
      if (
        effect.type !== "customDiscipline.action" ||
        !isTimerAction(effect.action) ||
        consumedTimerEffects.current.has(effect)
      )
        continue;
      consumedTimerEffects.current.add(effect);
      void behaviorTimerClient
        .command(effect.payload as TimerCommand)
        .catch((error) => console.error("Behavior timer:", error));
    }
  }, [renderModel, isEditor]);

  const renderNodeByKey = useMemo(() => {
    if (!renderModel) {
      return new Map<string, RenderNode>();
    }

    return collectRenderNodesByKey(renderModel.nodes);
  }, [renderModel]);

  const interactionIndex = useMemo(() => {
    for (const node of renderNodeByKey.values()) {
      if (node.clickable)
        return indexRuntimeInteractions(renderModel?.nodes ?? []);
    }
    return null;
  }, [renderModel, renderNodeByKey]);

  const interactionElement = (
    target: EventTarget | null,
    canvas: HTMLElement,
  ) => {
    if (!(target instanceof Element) || !canvas.contains(target)) return null;
    // Ignore events bubbling through a nested renderer or a tooltip portal.
    if (target.closest("[data-layout-canvas]") !== canvas) return null;
    if (target.closest("fieldset:disabled, [disabled], [aria-disabled='true']"))
      return null;
    return target.closest<HTMLElement>("[data-layout-node-key]");
  };

  const handleLayoutClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || !interactionIndex) return;
    const element = interactionElement(event.target, event.currentTarget);
    if (!element) return;
    const key = element.dataset.layoutNodeKey;
    if (key == null) return;
    for (const target of runtimeClickTargets(interactionIndex, key)) {
      dispatchCustomDisciplineClick(
        target.id,
        target.clickTargetKey ?? target.key ?? target.id,
      );
    }
  };

  const handleLayoutKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || (event.key !== "Enter" && event.key !== " "))
      return;
    const element = interactionElement(event.target, event.currentTarget);
    if (!element || event.target !== element) return;
    const entry = interactionIndex?.get(element.dataset.layoutNodeKey ?? "");
    if (!entry || !needsRuntimeButtonSemantics(entry.node)) return;
    event.preventDefault();
    if (
      !event.repeat &&
      ((event.key === "Enter" && event.type === "keydown") ||
        (event.key === " " && event.type === "keyup"))
    )
      element.click();
  };

  useLayoutEffect(() => {
    renderNodeByKeyRef.current = new Map(renderNodeByKey);
  }, [renderNodeByKey]);

  useLayoutEffect(() => {
    if (!controlRoomDispatches?.length) {
      return;
    }

    for (const dispatch of controlRoomDispatches) {
      committedControlRoomDispatchIdsRef.current.add(dispatch.id);
    }
  }, [controlRoomDispatches, renderModel]);

  useLayoutEffect(() => {
    const pending = customDisciplineClickDispatches.filter(
      (dispatch) =>
        !committedCustomDisciplineClickDispatchIdsRef.current.has(dispatch.id),
    );
    if (!pending.length) {
      if (!customDisciplineClickDispatches.length)
        committedCustomDisciplineClickDispatchIdsRef.current.clear();
      return;
    }
    // A host command can synchronously update parent props. Consume the click
    // before dispatching effects so that a new callback cannot replay it.
    for (const dispatch of pending)
      committedCustomDisciplineClickDispatchIdsRef.current.add(dispatch.id);
    if (onCustomDisciplineActions || onCustomDisciplineAction) {
      const actionEffects = (renderModel?.effects ?? []).filter(
        (effect): effect is CustomDisciplineActionEffect =>
          effect.type === "customDiscipline.action" &&
          !isTimerAction(effect.action),
      );
      if (onCustomDisciplineActions) onCustomDisciplineActions(actionEffects);
      else
        for (const effect of actionEffects) onCustomDisciplineAction?.(effect);
    }

    setCustomDisciplineClickDispatches((current) =>
      current.filter(
        (dispatch) =>
          !committedCustomDisciplineClickDispatchIdsRef.current.has(
            dispatch.id,
          ),
      ),
    );
  }, [
    customDisciplineClickDispatches,
    renderModel,
    onCustomDisciplineAction,
    onCustomDisciplineActions,
  ]);

  useLayoutEffect(() => {
    const animationEffects = (renderModel?.effects ?? []).filter(
      (effect): effect is AnimationPlayEffect =>
        effect.type === "animation.play",
    );

    if (animationEffects.length === 0) {
      return;
    }

    const playAnimations = () => {
      playCompiledAnimationEffects(animationEffects, {
        runtimeElementByKey: runtimeElementByKeyRef.current,
        renderNodeByKey: renderNodeByKeyRef.current,
        canvasElement: canvasRef.current,
        scale,
      });
    };

    playAnimations();

    const hasMissingTargets = animationEffects.some(
      (effect) => !runtimeElementByKeyRef.current.has(effect.targetNodeKey),
    );
    if (!hasMissingTargets) {
      return;
    }

    const retryFrameId = window.requestAnimationFrame(playAnimations);
    return () => {
      window.cancelAnimationFrame(retryFrameId);
    };
  }, [renderModel, scale]);

  useLayoutEffect(() => {
    if (!renderModel) {
      return;
    }

    for (const continuation of renderModel.continuations) {
      wakeContinuationsRef.current.set(continuation.key, continuation);
    }

    committedWakeVersionRef.current = wakeRun.version;
    scheduleNextWake();
  }, [renderModel, scheduleNextWake, wakeRun.version]);

  const mediaSources = useMemo(() => {
    if (!renderModel) {
      return [];
    }
    return collectMediaSources(renderModel.nodes);
  }, [renderModel]);

  const mediaCache = useProtectedMediaCache(mediaSources);

  const appearance = useMemo(
    () => compatibleVisualVariant(document, visualVariant),
    [document, visualVariant],
  );
  const presentedNodes = useMemo(
    () =>
      renderModel
        ? presentCompositionRuntimeColors(
            presentCatalogueRenderNodes(
              presentRenderNodes(renderModel.nodes, document, appearance),
              compositionAppearance,
            ),
            document,
            compositionColorMode,
          )
        : [],
    [
      renderModel,
      document,
      appearance,
      compositionColorMode,
      compositionAppearance,
    ],
  );
  const presentedCanvas =
    compositionAppearance?.patch.canvas ??
    appearance?.canvas ??
    renderModel?.canvas;

  const fillContextValue =
    useMemo<CompiledLayoutFillContextValue | null>(() => {
      if (!fillParent || !renderModel) {
        return null;
      }
      return {
        fillParent: true,
        designWidth: presentedCanvas!.width,
        designHeight: presentedCanvas!.height,
      };
    }, [fillParent, renderModel, presentedCanvas]);

  if (!renderModel) {
    return null;
  }

  return (
    <CompositionThemeContext.Provider value={catalogueTheme}>
      <ProtectedMediaCacheContext.Provider value={mediaCache}>
        <CompiledLayoutRuntimeProvider
          registerNodeElement={registerNodeElement}
          runtimeElementByKey={runtimeElementByKeyRef.current}
          renderNodeByKey={renderNodeByKeyRef.current}
          canvasElement={canvasRef.current}
          dispatchCustomDisciplineClick={dispatchCustomDisciplineClick}
          onCustomDisciplineInputChange={onCustomDisciplineInputChange}
          runtimeData={data}
        >
          <CompiledLayoutFillContext.Provider value={fillContextValue}>
            <div
              ref={canvasRef}
              data-layout-canvas="true"
              onClick={interactionIndex ? handleLayoutClick : undefined}
              onKeyDown={interactionIndex ? handleLayoutKey : undefined}
              onKeyUp={interactionIndex ? handleLayoutKey : undefined}
              style={{
                position: "relative",
                width: fillParent ? "100%" : presentedCanvas!.width * scale,
                height: fitContentHeight
                  ? "auto"
                  : fillParent
                    ? "100%"
                    : presentedCanvas!.height * scale,
                overflow: "hidden",
                background:
                  compositionAppearance?.patch.backgroundColor ??
                  appearance?.backgroundColor ??
                  document.scene.backgroundColor,
                ...canvasStyle,
              }}
            >
              {interactionIndex && <style>{RUNTIME_INTERACTION_CSS}</style>}
              {presentedNodes.map((node, index) => (
                <RenderNodeView
                  key={getSiblingRenderReactKey(node, index, renderModel.nodes)}
                  node={node}
                  scale={scale}
                  isSceneRoot={fillParent}
                />
              ))}
            </div>
          </CompiledLayoutFillContext.Provider>
        </CompiledLayoutRuntimeProvider>
      </ProtectedMediaCacheContext.Provider>
    </CompositionThemeContext.Provider>
  );
}

/**
 * Renders a layout document via the layout compiler with optional custom font loading.
 */
export function CompiledLayoutRenderer({
  document,
  data,
  scale = 1,
  isEditor = false,
  reuseProgram = false,
  fillParent = false,
  fitContentHeight = false,
  canvasStyle,
  visualVariant,
  compositionColorMode,
  catalogueTheme,
  compositionAppearance,
  controlRoomDispatches,
  getLayoutFontFaces: getLayoutFontFacesProp,
  runtimeInvalidation,
  onCustomDisciplineAction,
  onCustomDisciplineActions,
  onCustomDisciplineInputChange,
  nodeInstanceKeyPrefix,
}: CompiledLayoutRendererProps) {
  const catalogueAppearance = useMemo(
    () =>
      catalogueTheme
        ? readCompositionAppearance(
            document,
            catalogueTheme.mode,
            catalogueTheme.color,
            data.__customDisciplineLabels,
          )
        : undefined,
    [
      document,
      catalogueTheme?.mode,
      catalogueTheme?.color,
      data.__customDisciplineLabels,
    ],
  );
  document = localizeCustomDisciplineDocument(
    document,
    data.__customDisciplineLabels,
  );
  const getLayoutFontFacesFromDataProvider = useLayoutFontFacesProvider();
  const getLayoutFontFaces =
    getLayoutFontFacesProp ?? getLayoutFontFacesFromDataProvider;

  useLayoutFontFacesLoader(getLayoutFontFaces);

  return (
    <CompiledLayoutFontFacesProvider getLayoutFontFaces={getLayoutFontFaces}>
      <CompiledLayoutRendererInner
        document={document}
        data={data}
        scale={scale}
        isEditor={isEditor}
        reuseProgram={reuseProgram}
        fillParent={fillParent}
        fitContentHeight={fitContentHeight}
        canvasStyle={canvasStyle}
        visualVariant={visualVariant}
        compositionColorMode={compositionColorMode}
        catalogueTheme={catalogueTheme}
        compositionAppearance={catalogueAppearance ?? compositionAppearance}
        controlRoomDispatches={controlRoomDispatches}
        runtimeInvalidation={runtimeInvalidation}
        onCustomDisciplineAction={onCustomDisciplineAction}
        onCustomDisciplineActions={onCustomDisciplineActions}
        onCustomDisciplineInputChange={onCustomDisciplineInputChange}
        nodeInstanceKeyPrefix={nodeInstanceKeyPrefix}
      />
    </CompiledLayoutFontFacesProvider>
  );
}
